import { Button, Checkbox } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/system';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	AddQaAccessRequestCommand,
	CustomerDto,
	useProductCenterApi,
} from 'api';
import { getSingleBusinessPartnerForQaAccessQuery } from 'api/queries';
import { AccordionContainer } from 'library/components/controls/accordion/AccordionContainer';
import { AccordionItem } from 'library/components/controls/accordion/AccordionItem';
import { VolvoDataGrid } from 'library/components/controls/datagrid/VolvoDataGrid';
import {
	getAvailableRolesForQaAccess,
	selectADUser,
} from 'library/redux/application';
import { useAppDispatch, useAppSelector } from 'library/redux/hooks';
import { BusinessPartnerAccordionSection } from 'modules/Developers/BusinessPartnerAccordionSection';

import TextFieldWithCounter from './TextFieldWithCounter';

const FormSectionTitle = ({
	text,
	required,
	sx,
}: {
	text: string;
	required?: boolean;
	sx?: SxProps;
}) => (
	<Typography
		variant="h3"
		sx={Array.isArray(sx) ? [{ mb: 1 }, ...sx] : [{ mb: 1 }, sx]}>
		{required ? (
			<>
				<Box component="span" sx={{ color: 'error.main' }}>
					*
				</Box>{' '}
			</>
		) : null}
		{text}
	</Typography>
);

const excludedRolesNames = new Set([
	'PC_DEV',
	'SUPPORT_USER',
	'PRICE_MANAGER',
	'PUBLIC',
	'UPDATE_CONFIGURATION',
	'SUPER_USER',
]);

const defaultCustomerNumber = 99171185;

const mbAll = 6;
const requestingUserLanguage = 'en-US';
const justificationMinLength = 6;
const justificationMaxLength = 200;

interface QaAccessRoleRow {
	id: string;
	name: string;
	isSelected: boolean;
}

const updateRoleSelection = (
	rows: QaAccessRoleRow[],
	id: string | number,
	isSelected: boolean
): QaAccessRoleRow[] =>
	rows.map((row) => (row.id === id ? { ...row, isSelected } : row));

const QaAccessForm = (): React.JSX.Element => {
	const { t } = useTranslation([
		'error',
		'home',
		'common',
		'commonButton',
		'qaAccess',
	]);
	const api = useProductCenterApi().api;
	const { enqueueSnackbar } = useSnackbar();
	const dispatch = useAppDispatch();

	const [isRequestFormExpanded, setIsRequestFormExpanded] = useState(false);
	const [justification, setJustification] = useState<string>('');
	const [
		businessPartnerAccordionSectionHeader,
		setBusinessPartnerAccordionSectionHeader,
	] = useState<string>();
	const { data: defaultCustomer } = useQuery<CustomerDto | null>(
		getSingleBusinessPartnerForQaAccessQuery(api, defaultCustomerNumber)
	);
	const [selectedCustomer, setSelectedCustomer] =
		useState<CustomerDto | null>(null);
	const [clearBusinessPartnerSearchTick, setClearBusinessPartnerSearchTick] =
		useState(0);
	const [isDefaultCustomerSelected, setIsDefaultCustomerSelected] =
		useState(true);
	const [availableRoles, setAvailableRoles] = useState<string[]>([]);
	const [loadingRoles, setLoadingRoles] = useState(false);
	const [sendingRequest, setSendingRequest] = useState(false);

	const normalizedDefaultCustomer: CustomerDto | null =
		defaultCustomer ?? null;
	const requestingUser = useAppSelector(selectADUser);

	// For testing purposes:
	// const testUserCounter = 2;
	// const requestingUser: ADUser = {
	// 	id: `testID${testUserCounter}`,
	// 	name: `testUser${testUserCounter}`,
	// 	mail: `test.mail${testUserCounter}@volvo.com`,
	// };

	const isRequestingUserValid =
		Boolean(requestingUser.id) && Boolean(requestingUser.name);
	const isCustomerSelected = selectedCustomer?.customerNumber != null;
	const forcedDisplayedCustomer = selectedCustomer;
	const isJustificationValid =
		justification.trim() !== '' &&
		justification.length > 0 &&
		justification.length >= justificationMinLength;

	const centeredAccordionHeaderSx = {
		width: isRequestFormExpanded ? '100%' : 'fit-content',
		mx: 'auto',
		'& > .MuiAccordion-heading .MuiAccordionSummary-content': {
			justifyContent: isRequestFormExpanded ? 'center' : 'flex-start',
		},
		'& > .MuiAccordion-heading .MuiAccordionSummary-content .MuiTypography-root':
			{
				textAlign: isRequestFormExpanded ? 'center' : 'left',
				width: isRequestFormExpanded ? '100%' : 'auto',
				pl: isRequestFormExpanded ? 3 : 0,
			},
	};
	useEffect(() => {
		if (!isRequestFormExpanded || availableRoles.length > 0) {
			return;
		}

		const fetchRoles = async () => {
			setLoadingRoles(true);

			try {
				const result = await dispatch(
					getAvailableRolesForQaAccess()
				).unwrap();
				const excludedRoles = (result ?? []).filter(
					(roleName: string) => !excludedRolesNames.has(roleName)
				);
				setAvailableRoles(excludedRoles || []);
			} catch (error) {
				console.error(
					'Failed to fetch available QA access roles',
					error
				);

				setAvailableRoles([]);
				enqueueSnackbar(t('qaAccess:form-available-roles-error'), {
					variant: 'error',
					autoHideDuration: 5000,
				});
			} finally {
				setLoadingRoles(false);
			}
		};
		void fetchRoles();
	}, [
		t,
		availableRoles.length,
		dispatch,
		isRequestFormExpanded,
		enqueueSnackbar,
	]);

	const allRolesGridData = useMemo(
		() =>
			availableRoles.map((roleName) => ({
				id: roleName,
				name: roleName,
				isSelected: false,
			})),
		[availableRoles]
	);

	const [rows, setRows] = useState(allRolesGridData);
	const hasSelectedRoles = rows.some((row) => row.isSelected === true);

	useEffect(() => {
		setRows(allRolesGridData);
	}, [allRolesGridData]);

	useEffect(() => {
		const handleCustomerChange = () => {
			if (
				selectedCustomer?.customerNumber ===
				normalizedDefaultCustomer?.customerNumber
			) {
				setBusinessPartnerAccordionSectionHeader(
					t('qaAccess:form-default-business-partner')
				);
				setIsDefaultCustomerSelected(true);
			} else if (
				selectedCustomer?.customerNumber !==
				normalizedDefaultCustomer?.customerNumber
			) {
				setBusinessPartnerAccordionSectionHeader(
					t('qaAccess:form-current-business-partner')
				);
				setIsDefaultCustomerSelected(false);
			}
		};
		handleCustomerChange();
	}, [selectedCustomer, normalizedDefaultCustomer, t]);

	const handleCheckboxChange = useCallback(
		(id: string | number) =>
			(event: React.ChangeEvent<HTMLInputElement>) => {
				const isSelected = event.target.checked;
				setRows((currentRows) =>
					updateRoleSelection(currentRows, id, isSelected)
				);
			},
		[]
	);

	const selectedRoles = useMemo(
		() => rows.filter((row) => row.isSelected).map((row) => row.name),
		[rows]
	);

	const clearSelectedRoles = () => {
		setRows((currentRows) =>
			currentRows.map((row) => ({ ...row, isSelected: false }))
		);
	};

	const onChangeJustification = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const value = event.target.value;
		setJustification(value);
	};

	const columns = useMemo(
		(): GridColDef[] => [
			{
				field: 'name',
				headerName: t('common:role-name'),
				minWidth: 200,
				flex: 2,
			},
			{
				field: 'isSelected',
				headerName: t('common:assigned'),
				type: 'boolean',
				minWidth: 100,
				flex: 1,
				renderCell: (params) => (
					<Checkbox
						checked={!!params.row.isSelected}
						onChange={handleCheckboxChange(params.row.id)}
						size="medium"
						sx={{ '& .MuiSvgIcon-root': { fontSize: 32 } }}
					/>
				),
				sortable: false,
				filterable: false,
			},
		],
		[t, handleCheckboxChange]
	);

	const clearAndCloseForm = () => {
		setJustification('');
		clearSelectedRoles();
		setSelectedCustomer(normalizedDefaultCustomer);
		setIsRequestFormExpanded(false);
	};

	const handleConflictError = (exceptionType: unknown) => {
		switch (exceptionType) {
			case 'UserAlreadyExistsException':
				enqueueSnackbar(t('qaAccess:form-user-already-exists-error'), {
					variant: 'error',
					autoHideDuration: 5000,
				});
				break;
			case 'PendingQaAccessRequestAlreadyExistsException':
				enqueueSnackbar(
					t('qaAccess:form-existing-pending-request-error'),
					{
						variant: 'warning',
						autoHideDuration: 5000,
					}
				);
				break;
		}

		clearAndCloseForm();
	};

	const handleRequestError = (error: unknown) => {
		if (!axios.isAxiosError(error)) {
			enqueueSnackbar(t('qaAccess:form-unexpected-sending-error'), {
				variant: 'error',
				autoHideDuration: 5000,
			});
			return;
		}

		const errorStatus = error.response?.status;
		if (errorStatus === 409) {
			const exceptionType: unknown =
				error.response?.data?.invalidItems?.[0]?.propertyName;
			handleConflictError(exceptionType);
			return;
		}

		if (errorStatus === 400) {
			enqueueSnackbar(t('qaAccess:form-invalid-data-error'), {
				variant: 'error',
				autoHideDuration: 5000,
			});
			return;
		}

		enqueueSnackbar(t('qaAccess:form-unexpected-sending-error'), {
			variant: 'error',
			autoHideDuration: 5000,
		});
	};

	const handleSendRequest = async () => {
		const customerNumber = selectedCustomer?.customerNumber;
		if (customerNumber == null) {
			enqueueSnackbar(t('qaAccess:form-customer-error'), {
				variant: 'error',
				autoHideDuration: 5000,
			});
			return;
		}

		const requestPayload: AddQaAccessRequestCommand = {
			userName: requestingUser.name,
			userId: requestingUser.id,
			userLanguage: requestingUserLanguage,
			customerNumber,
			justification,
			requestedRoles: selectedRoles,
		};

		setSendingRequest(true);
		try {
			await api.addQaAccessRequest(requestPayload);
			clearAndCloseForm();
			enqueueSnackbar(t('qaAccess:form-request-sent-success'), {
				variant: 'success',
				autoHideDuration: 4000,
			});
		} catch (error) {
			handleRequestError(error);
		} finally {
			setSendingRequest(false);
		}
	};

	const fulfilledConditionsCount = [
		hasSelectedRoles,
		isJustificationValid,
		isCustomerSelected,
	].filter(Boolean).length;

	const areAllConditionsFulfilled = fulfilledConditionsCount === 3;

	return (
		<Box sx={{}}>
			<AccordionContainer
				defaultExpanded={
					isRequestFormExpanded ? 'request-form' : undefined
				}>
				<AccordionItem
					accordionName="request-form"
					header={
						isRequestFormExpanded
							? t('qaAccess:form-hide')
							: t('qaAccess:form-expand')
					}
					onChange={(_, isExpanded) =>
						setIsRequestFormExpanded(!isExpanded)
					}
					sx={centeredAccordionHeaderSx}>
					<Box>
						<Grid container>
							<Grid size={{ sm: 6 }} sx={{ mb: mbAll }}>
								<Typography
									variant="h2"
									sx={{
										textAlign: 'left',
									}}>
									{t('qaAccess:form-provide-info-header')}
								</Typography>
							</Grid>
							<Grid size={{ sm: 6 }}>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'flex-end',
										alignItems: 'center',
										gap: 1,
										opacity: 0.5,
									}}>
									<Typography variant="subtitle1">
										{'UserID:'}
									</Typography>
									<Typography variant="body1">
										{requestingUser.id}
									</Typography>
								</Box>
							</Grid>
						</Grid>
						<Box>
							<Box sx={{ mb: mbAll }}>
								<FormSectionTitle
									text={t(
										'qaAccess:form-justification-header'
									)}
									required
								/>
								<TextFieldWithCounter
									text={justification}
									charMin={justificationMinLength}
									charMax={justificationMaxLength}
									onChange={onChangeJustification}
								/>
							</Box>
							<Box sx={{ mb: mbAll + 2 }}>
								<FormSectionTitle
									text={t('qaAccess:form-roles-header')}
									required
								/>
								<VolvoDataGrid
									isLoading={loadingRoles}
									rows={rows}
									columns={columns}
									hideFooter
									sx={{ marginRight: 2 }}
								/>
							</Box>
							<Box sx={{ mb: mbAll }}>
								<FormSectionTitle
									text={t(
										'qaAccess:form-business-partner-header'
									)}
									required
								/>
								<Box sx={{ display: 'flex' }}>
									<Button
										variant="outlined"
										size="small"
										color="primary"
										disabled={isDefaultCustomerSelected}
										onClick={() => {
											setClearBusinessPartnerSearchTick(
												(prev) => prev + 1
											);
											setSelectedCustomer(
												normalizedDefaultCustomer
											);
										}}
										sx={{
											marginBottom: 2,
											marginRight: 2,
										}}>
										{t(
											'qaAccess:form-restore-default-business-partner'
										)}
									</Button>
								</Box>
								<BusinessPartnerAccordionSection
									padding={0}
									qaAccessForm={true}
									showCountryCode={false}
									assignedCustomerHeader={
										businessPartnerAccordionSectionHeader
									}
									onlySelectionOfCustomer
									onCustomerSelected={setSelectedCustomer}
									initialDisplayedCustomer={
										normalizedDefaultCustomer
									}
									forcedDisplayedCustomer={
										forcedDisplayedCustomer
									}
									clearSearchTrigger={
										clearBusinessPartnerSearchTick
									}
								/>
							</Box>
						</Box>
						<Grid
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								marginTop: 8,
							}}>
							<Button
								variant="outlined"
								size="large"
								color="primary"
								onClick={() => setIsRequestFormExpanded(false)}
								disabled={sendingRequest}>
								{t('commonButton:cancel')}
							</Button>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 2,
								}}>
								<Typography
									color={
										areAllConditionsFulfilled
											? 'success.main'
											: 'error.main'
									}
									sx={{
										fontSize: 12,
										opacity: 0.5,
									}}>
									{`${fulfilledConditionsCount}/3`}
								</Typography>
								<Button
									variant="contained"
									color="primary"
									size="large"
									onClick={() => void handleSendRequest()}
									disabled={
										loadingRoles ||
										!isRequestingUserValid ||
										!areAllConditionsFulfilled ||
										sendingRequest
									}>
									{t('commonButton:send')}
								</Button>
							</Box>
						</Grid>
					</Box>
				</AccordionItem>
			</AccordionContainer>
		</Box>
	);
};

export default QaAccessForm;
